import DynamicSectionElement from '@/js/dynamic-section-element';

class FilterableSection extends DynamicSectionElement {
  constructor() {
    super();

    this.filterFormQuery = '[data-filter-form]';
    this.rootUrl = globalThis.location.pathname;
    if (!this.querySelector(this.filterFormQuery)) return;
    this.setFilterListeners();
  }

  removeEmptyParams(parameters) {
    const keysForDel = [];
    for (const [key, value] of parameters.entries()) {
      if (value == '') {
        keysForDel.push(key);
      }
    }

    for (const key of keysForDel) {
      parameters.delete(key);
    }
    return parameters;
  }

  /**
   *
   * @param {Element} filterForm (optional)
   * @returns {String} the URL to load the section from
   */
  calculateSectionRenderingUrl(filterForm) {
    let searchParameters;
    if (filterForm) {
      const formData = new FormData(filterForm);
      searchParameters = this.removeEmptyParams(new URLSearchParams(formData));
    } else {
      searchParameters = new URLSearchParams();

      // when resetting the filters, we need to persist these named existing URL params
      const searchParameterKeys = new Set(['q']);
      const initialUrlSearchParameters = new URLSearchParams(
        globalThis.location.search
      );

      // iterate through the parameters in urlSearchParams
      // if they are in the searchParamKeys array,
      // append them to filterSearchParameters
      // This ensures that on the search page, the search term is
      for (const key of initialUrlSearchParameters.keys()) {
        if (searchParameterKeys.has(key)) {
          searchParameters.append(key, initialUrlSearchParameters.get(key));
        }
      }
    }

    const href = `${this.rootUrl}?${searchParameters.toString()}`;
    return href;
  }

  /**
   * Gets the URL to load the section from, and calls onFilterFormSubmit
   * Used for example when updating the search query term from the search-header
   * Section re-rendering replaces entire section (ignores data-slots) as there may be
   * changes in which filter groups are present.
   * @returns {void}
   */
  applyFilters() {
    const filterForm = this.querySelector(this.filterFormQuery);
    const href = this.calculateSectionRenderingUrl(filterForm);
    this.onFilterFormSubmit(href, { replaceEntireSection: true });
  }

  /**
   * Listens for changes in the filters form, gets the calculated URL for section rendering, hands off to onFilterFormSubmit
   * @returns {void}
   */
  setFilterListeners() {
    const filterForm = this.querySelector(this.filterFormQuery);
    const filterInputs = filterForm.querySelectorAll('input');

    for (const input of filterInputs) {
      input.addEventListener('change', () => {
        const href = this.calculateSectionRenderingUrl(filterForm);
        this.onFilterFormSubmit(href);
      });
    }

    // remove single filters
    // very handily, shopify provides us with a URL that will remove a single filter, so we can just pass that to section rendering.
    const removeButtons = this.querySelectorAll('[data-remove-single-filter]');
    for (const item of removeButtons) {
      item.addEventListener('click', event => {
        event.preventDefault();
        this.loadSectionFromUrl(item.href, {
          replaceState: true
        });
      });
    }

    // Reset all button
    const filterResetButton = this.querySelector('[data-filters-reset]');
    if (!filterResetButton) return;
    filterResetButton.addEventListener('click', () => {
      const resetHref = this.calculateSectionRenderingUrl();
      this.onFilterFormSubmit(resetHref);
    });
  }

  /**
   * Calls section rendering with the URL provided
   * @param {String} href - the URL to load the section from
   * @param {Object} options
   * @param {Boolean} options.replaceEntireSection - whether to replace the entire section or just the slot
   */
  async onFilterFormSubmit(href, { replaceEntireSection = false } = {}) {
    await this.loadSectionFromUrl(href, {
      replaceState: true,
      replaceEntireSection
    });

    this.updateSearchHeader(href);
  }

  /**
   * Reloads the search header section.
   * @returns {void}
   */
  async updateSearchHeader(href) {
    const searchHeader = document.querySelector('search-header');
    if (!searchHeader) return;
    searchHeader.loadSectionFromUrl(href);
  }

  /**
   * After the section is re-rendered, re-attach the listeners to the filter form
   * @returns {void}
   */
  afterSectionRender() {
    // the filters are re-rendered after each filter change,
    this.setFilterListeners();
  }
}

customElements.get('filterable-section') ||
  customElements.define('filterable-section', FilterableSection);

//  also exporting this so it can be extended in other elements too.
export default FilterableSection;
